import {Component, ViewChild} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {
    AccountResponse,
    EntityType,
    ReferenceBookResponse,
    SubjectApiService,
    SubjectCategory,
    SubjectResponse,
    SubjectType,
} from '@npck/af-frontend-api';
import {
    formatDateTimeFromServerToUi,
    formatFromServerToUi,
    LocalizationService,
    NESTED_TAB,
    RouterService,
    rxSpinner,
    TAB,
} from '@npck/libs/commons';
import {DialogService, SnackbarService} from '@npck/libs/ui-kit';
import dayjs from 'dayjs';
import {EMPTY, mergeMap, zip} from 'rxjs';
import {Column, ColumnType, ELLIPSIS_LARGE_TEXT_MAX_LENGTH, Row, StatusColumn} from '../../../components/full-table/table/table.component';
import {mapIncidentStatusToStyle} from '../../../components/status/status.component';
import {AuthHolderService} from '../../../domain/auth-holder.service';
import {NESTED_NESTED_TAB} from '../../../domain/constants';
import {DictionaryHolderService} from '../../../domain/dictionary-holder.service';
import {formatPhone} from '../../../domain/phone-utils';
import {INCIDENT_ID, SUBJECT_ID} from '../../../domain/query-params.keys';
import {MainContainerComponent} from '../../../layout/main-container/main-container.component';
import {Path} from '../../../path';
import {AF_CENTRE_TABS} from '../../af-centre/af-centre.component';
import {SubjectsTab} from '../subjects.component';
import {
    DeleteSubjectCategoryDialogComponent,
    DeleteSubjectCategoryDialogData,
    DeleteSubjectCategoryDialogResult,
} from './delete-subject-category-dialog/delete-subject-category-dialog.component';
import {ResolutionInfoDialogComponent, ResolutionInfoDialogData} from './resolution-info-dialog/resolution-info-dialog.component';

@Component({
    templateUrl: 'subject-details.component.html',
    styleUrls: ['subject-details.component.scss'],
    standalone: false,
})
export class SubjectDetailsComponent {
    @ViewChild('mainContainerComponent') mainContainerComponent: MainContainerComponent;
    EntityType = EntityType;
    subject: SubjectResponse;
    subjectId: string;
    incidentId: string;
    subjectFields: FieldInfo[];
    creatorFields: FieldInfo[];
    personSubjectFields: (keyof SubjectResponse)[] = [
        'surname', 'name', 'patronymic', 'gender', 'identification_number', 'country', 'origin_date',
    ];
    organizationSubjectFields: (keyof SubjectResponse)[] = [
        'name', 'identification_number', 'type', 'country', 'origin_date',
    ];
    creatorFieldNames: FieldsType = [
        'source_organization', 'created_by', 'created_date', 'updated_by',
        'updated_date', 'deleted_by', 'deleted_date', 'creation_method',
    ];
    countries: ReferenceBookResponse[];
    gender: ReferenceBookResponse[];
    tabs: SubjectDetailsTab[] = [];
    selectedTabIndex: number;
    activeTab: SubjectDetailsTab;
    showPotentialVictimView = false;
    basisExpanded = false;
    potentialVictimTabs: PotentialVictimTab[] = ['tabContacts', 'tabCategories', 'tabHistory'];
    activePotentialVictimTab: PotentialVictimTab = 'tabContacts';
    datasource: Row[];
    binDictionary: ReferenceBookResponse[] = [];
    private readonly backToTab: SubjectsTab;
    private readonly preselectedCategory: SubjectCategory | null;

    constructor(
        private activatedRoute: ActivatedRoute,
        private routerService: RouterService,
        private subjectApiService: SubjectApiService,
        private localizationService: LocalizationService,
        private dialogService: DialogService,
        private snackbarService: SnackbarService,
        private dictionaryHolderService: DictionaryHolderService,
        private authHolderService: AuthHolderService,
    ) {
        this.incidentId = this.activatedRoute.snapshot.paramMap.get(INCIDENT_ID);
        this.subjectId = this.activatedRoute.snapshot.paramMap.get(SUBJECT_ID);
        this.backToTab = this.activatedRoute.snapshot.queryParamMap.get(TAB) as SubjectsTab;
        this.showPotentialVictimView = this.backToTab as string === 'tabPotentialVictim';
        this.preselectedCategory = this.activatedRoute.snapshot.queryParamMap.get('category') as SubjectCategory | null;
        this.dictionaryHolderService.getReference('bin').subscribe(items => {
            this.binDictionary = items.filter(item => item.code1 === 'BANK');
        });
        zip([
            this.dictionaryHolderService.getReference('country'),
            this.dictionaryHolderService.getReference('gender'),
            this.subjectApiService.getSubject(this.subjectId),
        ])
            .pipe(rxSpinner('initial'))
            .subscribe(([countries, gender, response]) => {
                this.countries = countries;
                this.gender = gender;
                this.subject = response;

                this.subject.incidents.length && this.tabs.push('tabIncidents');
                this.tabs.push('tabCategories');
                this.tabs.push('tabHistory');
                if ((this.subject.ibans?.length ?? 0) > 0) {
                    this.tabs.push('tabIban');
                }
                if ((this.subject.foreignIbans?.length ?? 0) > 0) {
                    this.tabs.push('tabForeignIban');
                }
                if (this.subject.wallets?.length) {
                    this.tabs.push('tabWallets');
                }
                if (this.subject.paymentPoints?.length) {
                    this.tabs.push('tabPaymentPoint');
                }
                this.subject.phone_numbers.length && this.tabs.push('tabPhone');
                this.subject.card_numbers.length && this.tabs.push('tabCards');
                // this.tabs.push('tabPassport');
                if (this.subject.phone_numbers?.length) {
                    this.potentialVictimTabs = [...this.potentialVictimTabs, 'tabPhone'];
                }
                const nestedTab = (this.incidentId
                    ? this.activatedRoute.snapshot.queryParamMap.get(NESTED_NESTED_TAB)
                    : this.activatedRoute.snapshot.queryParamMap.get(NESTED_TAB)) as SubjectDetailsTab;
                this.activeTab = this.tabs.includes(nestedTab) ? nestedTab : this.tabs[0];
                this.selectedTabIndex = this.tabs.findIndex(tab => tab === this.activeTab);
                this.handleNavigation(this.selectedTabIndex);


                let fields: FieldsType;
                if (response.subjectType === SubjectType.PERSON) {
                    fields = this.personSubjectFields;
                } else if (response.subjectType === SubjectType.ORGANIZATION) {
                    fields = this.organizationSubjectFields;
                }
                this.subjectFields = this.mapFieldToValue(fields, response, response.subjectType);
                this.creatorFields = this.mapFieldToValue(this.creatorFieldNames, response);
            });
    }

    get canDelete(): boolean {
        return this.authHolderService.hasAccess(['npck-admin']);
    }

    get potentialVictimCategory(): {creationReason?: string; creationDescription?: string} | null {
        if (!this.subject) {
            return null;
        }
        return {
            creationReason: this.subject.creationReason,
            creationDescription: this.subject.creationDescription,
        };
    }

    get hasActivePotentialVictim(): boolean {
        return !!this.subject?.categories?.some(
            c => c.category === SubjectCategory.POTENTIAL_VICTIM && !c.isDeleted,
        );
    }

    get isAuthor(): boolean {
        const orgBin = this.authHolderService.organizationBin;
        if (!orgBin) {
            return false;
        }
        const pvCategory = this.subject?.categories
            ?.find(c => c.category === SubjectCategory.POTENTIAL_VICTIM && !c.isDeleted);
        return pvCategory?.createdByOrganization === orgBin;
    }

    get canShowSubjectEdit(): boolean {
        return !this.subject?.is_deleted;
    }

    get canShowSubjectDelete(): boolean {
        if (this.subject?.is_deleted) {
            return false;
        }
        return this.canDelete;
    }

    toggleBasis(): void {
        this.basisExpanded = !this.basisExpanded;
    }

    selectPotentialVictimTab(index: number): void {
        this.activePotentialVictimTab = this.potentialVictimTabs[index];
        if (this.activePotentialVictimTab === 'tabPhone') {
            this.buildDatasourceForTab('tabPhone');
        }
    }

    editSubject(): void {
        this.routerService.navigate(
            `${Path.SUBJECTS}/${this.subjectId}/edit`,
            {queryParams: {[TAB]: this.backToTab}},
        );
    }

    customBackNavigation = (path: string): boolean | void => {
        if (!this.incidentId && path === Path.SUBJECTS && AF_CENTRE_TABS.includes(this.backToTab)) {
            this.routerService.navigate(Path.AF_CENTRE, {queryParams: {[TAB]: this.backToTab}});
            return;
        }
        return true;
    };

    breadcrumbCustomizer = (path: string): string => {
        if (this.incidentId === path) {
            return this.localizationService.getLocalizedKey('i18n_lib_breadcrumb#IncidentId', {[INCIDENT_ID]: this.incidentId});
        }
        if (this.subjectId === path) {
            return this.localizationService.getLocalizedKey('i18n_lib_breadcrumb#SubjectId', {fraudId: this.subjectId});
        }
        return path;
    };

    handleNavigation($event: number): void {
        this.activeTab = this.tabs[$event];
        if (this.incidentId) {
            this.routerService.navigate(
                Path.INCIDENTS_SUBJECT_DETAILS,
                {
                    pathParams: {
                        [INCIDENT_ID]: this.incidentId,
                        [SUBJECT_ID]: this.subjectId,
                    },
                    queryParams: {[NESTED_NESTED_TAB]: this.activeTab},
                    queryParamsHandling: 'merge',
                });
        } else {
            this.routerService.navigate(
                Path.SUBJECT_DETAILS,
                {
                    pathParams: {[SUBJECT_ID]: this.subjectId},
                    queryParams: {[TAB]: this.backToTab, [NESTED_TAB]: this.activeTab},
                    queryParamsHandling: 'merge',
                });
        }
        this.buildDatasourceForTab(this.activeTab);
    }

    openDeleteItemDialog(): void {
        const category = this.resolveCategoryFromTab();
        if (!category) {
            return;
        }
        const dialogData: DeleteSubjectCategoryDialogData = {
            category,
            categoryName: this.localizationService.getLocalizedKey(`i18n_af_category#${category}`),
            subjectType: this.subject.subjectType,
            identificationNumber: this.subject.iin ?? this.subject.bin,
        };
        this.dialogService
            .openCustomDialog<DeleteSubjectCategoryDialogComponent, DeleteSubjectCategoryDialogResult>(
                DeleteSubjectCategoryDialogComponent,
                dialogData,
            )
            .pipe(
                mergeMap(result => result
                    ? this.subjectApiService.deleteSubject(this.subject.id, {
                        category,
                        deletionReason: result.deletionReason,
                        description: result.description,
                    }).pipe(rxSpinner('deleting'))
                    : EMPTY),
            )
            .subscribe(() => {
                this.snackbarService.showSnackBar('i18n_af_snackBarMessageFraudSubjectCategoryDeleted');
                this.navigateBackToSubjectsTab();
            });
    }

    private navigateBackToSubjectsTab(): void {
        if (this.incidentId) {
            this.mainContainerComponent.breadcrumbs.navigateBack();
            return;
        }
        this.routerService.navigate(
            AF_CENTRE_TABS.includes(this.backToTab) ? Path.AF_CENTRE : Path.SUBJECTS,
            {queryParams: {[TAB]: this.backToTab}}
        );
    }

    private resolveCategoryFromTab(): SubjectCategory | null {
        if (this.preselectedCategory) {
            return this.preselectedCategory;
        }
        switch (this.backToTab as string) {
            case 'tabVictim':
                return SubjectCategory.VICTIM;
            case 'tabDropper':
                return SubjectCategory.DROPPER;
            case 'tabPotentialVictim':
                return SubjectCategory.POTENTIAL_VICTIM;
            case 'tabWhiteList':
                return SubjectCategory.WHITE_LIST;
            default:
                return null;
        }
    }

    private buildDatasourceForTab(tab: SubjectDetailsTab): void {
        this.datasource = [];
        switch (tab) {
            case 'tabPaymentPoint':
                this.datasource = this.subject.paymentPoints.map(paymentPoint => <Row>{
                    paymentPoint: <Column>{
                        type: ColumnType.TEXT,
                        text: paymentPoint.number,
                    },
                    ...this.createRow(paymentPoint),
                });
                break;
            case 'tabWallets':
                this.datasource = this.subject.wallets.map(wallet => <Row>{
                    wallet: <Column>{
                        type: ColumnType.TEXT,
                        text: wallet.number,
                    },
                    ...this.createRow(wallet),
                });
                break;
            case 'tabIban':
                this.datasource = (this.subject.ibans || []).map(iban => <Row>{
                    ...this.buildIbanRow(iban),
                });
                break;

            case 'tabForeignIban':
                this.datasource = (this.subject.foreignIbans || []).map(iban => <Row>{
                    ...this.buildIbanRow(iban),
                });
                break;
            case 'tabCards':
                this.datasource = this.subject.card_numbers.map(cardNumber => <Row>{
                    cardNumber: <Column>{
                        type: ColumnType.TEXT,
                        text: this.formatCardNumber(cardNumber.card_number),
                    },
                    ...this.createRow(cardNumber),
                });
                break;
            case 'tabIncidents':
                this.datasource = this.subject.incidents.map(incident => {
                    const relatedOrganization = incident.relatedOrganization?.join(', ');
                    return <Row>{
                        incidentId: <Column>{
                            type: ColumnType.LINK,
                            text: incident.incidentId,
                            click: () => this.navigateToIncidents(incident.incidentId),
                        },
                        relatedOrganization: <Column>{
                            type: ColumnType.TEXT,
                            text: relatedOrganization,
                            cellStyle: relatedOrganization?.length > ELLIPSIS_LARGE_TEXT_MAX_LENGTH && {
                                ellipsisTextLarge: true,
                            },
                            tooltip: relatedOrganization?.length > ELLIPSIS_LARGE_TEXT_MAX_LENGTH && {
                                text: relatedOrganization,
                            },
                        },
                        authorOrganization: <Column>{
                            type: ColumnType.TEXT,
                            text: incident.authorOrganization,
                        },
                        status: <StatusColumn>{
                            type: ColumnType.STATUS,
                            text: incident.status,
                            style: mapIncidentStatusToStyle(incident.status),
                        },
                        resolutionStatus: <Column>{
                            type: ColumnType.BUTTON,
                            text: incident.resolutionStatus,
                            iconPosition: 'right',
                            icon: 'ic_popup',
                            click: () => {
                                this.dialogService.openCustomDialogFitContent(ResolutionInfoDialogComponent, <ResolutionInfoDialogData>{
                                    incidentId: incident.incidentId,
                                    identificationNumber: this.subject.iin ?? this.subject.bin,
                                },
                                    {
                                        panelClass: ['max-height-90', 'ui-kit-dialog-w-fit-content'],
                                    });
                            },
                        },
                        createdDate: <Column>{
                            type: ColumnType.DATE_TIME_SECONDS,
                            text: incident.creationDate,
                        },
                    };
                });
                break;
            case 'tabPhone':
                this.datasource = this.subject.phone_numbers.filter(phoneNumber => phoneNumber.number?.length >= 9).map(phoneNumber => {
                    return <Row>{
                        phone: <Column>{
                            type: ColumnType.TEXT,
                            text: formatPhone(phoneNumber.number),
                        },
                        ...this.createRow(phoneNumber),
                    };
                });
                break;
        }
    }

    private formatCardNumber(input: string): string {
        const regex = /.{1,4}/g;
        const result = input.match(regex);
        return result?.join(' ') || input;
    }

    private createRow(item: any): Row {
        const row: Row = {};
        if (item.incident_id) {
            row.incidentId = <Column>{
                type: ColumnType.LINK,
                text: item.incident_id,
                click: () => this.navigateToIncidents(item.incident_id),
            };
        }
        if (item.created_by) {
            row.createdBy = <Column>{
                type: ColumnType.TEXT,
                text: item.created_by,
            };
        }
        if (item.created_date) {
            row.createdDate = <Column>{
                type: ColumnType.DATE_TIME_SECONDS,
                text: item.created_date,
            };
        }
        return row;
    }

    private buildIbanRow(iban: AccountResponse): Partial<Row> {
        const match = this.binDictionary.find(
            i => iban.issuerBin && (i.code2 === iban.issuerBin || i.code === iban.issuerBin),
        );
        const issuer = match ? match.name_ru : iban.issuerBin;
        return {
            iban: <Column>{type: ColumnType.TEXT, text: iban.iban},
            issuerBin: <Column>{type: ColumnType.TEXT, text: issuer},
            incidentId: <Column>{
                type: ColumnType.LINK,
                text: iban.incident_id,
                click: () => this.navigateToIncidents(iban.incident_id),
            },
            createdBy: <Column>{type: ColumnType.TEXT, text: iban.created_by},
            authorOrganization: <Column>{type: ColumnType.TEXT, text: iban.authorOrganization},
            createdDate: <Column>{type: ColumnType.DATE_TIME_SECONDS, text: iban.created_date},
        };
    }

    private navigateToIncidents(incidentId: string): void {
        this.routerService.navigate(`${Path.INCIDENTS}/${incidentId}`);
    }

    private mapFieldToValue(fields: FieldsType, response: ResponseType, subjectType?: SubjectType): FieldInfo[] {
        return fields.map(field => {
            let modifiedFiledName: string;
            let value: any = (response as any)[field];
            if (field === 'name') {
                modifiedFiledName = subjectType === SubjectType.PERSON ? 'firstname' : 'title';
            } else if (field === 'origin_date') {
                value = value && formatFromServerToUi(dayjs(value));
                modifiedFiledName = subjectType === SubjectType.PERSON ? 'birth_date' : 'registered_date';
            } else if (['created_date', 'updated_date', 'deleted_date'].includes(field)) {
                value = value && formatDateTimeFromServerToUi(dayjs(value));
            } else if (field === 'country') {
                value = this.countries.find(country => country.code === value ||
                    country.code1 === value ||
                    country.code2 === value)?.name_ru || value;
            } else if (field === 'gender' && !!value) {
                value = this.gender.find(gender => gender.code === value ||
                    gender.code1 === value ||
                    gender.code2 === value)?.name_ru || value;
            } else if (field === 'is_deleted') {
                const key = value as boolean ? 'i18n_af_filter_field_status#deleted' : 'i18n_af_filter_field_status#active';
                value = this.localizationService.getLocalizedKey(key);
            } else if (field === 'creation_method') {
                value = value && this.localizationService.getLocalizedKey(`i18n_af_creationMethod#${value}`);
            } else if (field === 'categories') {
                const list = Array.isArray(value) ? value : [];
                const labels = list
                    .map((item: SubjectCategory | {category?: SubjectCategory | null}) => {
                        const key = typeof item === 'string' ? item : item?.category;
                        return key ? this.localizationService.getLocalizedKey(`i18n_af_category#${key}`) : null;
                    })
                    .filter((x): x is string => !!x);
                value = labels.length ? labels.join(', ') : '—';
            } else if (field === 'identification_number') {
                modifiedFiledName = subjectType === SubjectType.ORGANIZATION ? 'bin' : 'iin';
            }
            return {
                filedName: modifiedFiledName || field,
                fieldValue: value,
            };
        })
            .filter(field => !!field.fieldValue);
    }
}

declare type SubjectDetailsTab =
    'tabIban' | 'tabForeignIban' | 'tabPhone' | 'tabCards' | 'tabWallets' | 'tabPaymentPoint' | 'tabPassport' | 'tabIncidents'
    | 'tabCategories' | 'tabHistory';

declare type PotentialVictimTab = 'tabContacts' | 'tabCategories' | 'tabHistory' | 'tabRequests' | 'tabPhone';

declare type ResponseType = SubjectResponse;
declare type FieldsType = (keyof SubjectResponse)[];

export interface FieldInfo {
    filedName: string;
    fieldValue: string;
    showField?: boolean;
}
